import { addDays, subDays } from "date-fns"

import { StatusColor } from "~/constants/status"
import {
  convertToStartOfWeekUTC,
  differenceInWeeksUTC,
  getStartOfCurrentWeekUTC,
} from "~/utils/datetime"
import type { IVariantSummary } from "~/hooks/useVariantTableData"

/**
 * The NOOS sales window. A variant is Introduced until this many weeks have passed since its start,
 * i.e. until its own sales cover a full window. Move it together with the baseline window.
 */
export const INTRODUCED_WINDOW_WEEKS = 52

export function isInactiveStatus(status: string | null | undefined): boolean {
  return status === "phasedOut" || status === "phasingOut"
}

function everyEntry(
  entries: Pick<IVariantSummary, "settings">[],
  predicate: (status: string | null | undefined) => boolean,
): boolean {
  return entries.length > 0 && entries.every((e) => predicate(e?.settings?.lifecycleStatus))
}

export function isLifecyclePhasedOut(entries: Pick<IVariantSummary, "settings">[]): boolean {
  return everyEntry(entries, (status) => status === "phasedOut")
}

function isPhasedOutStatus(status: string | null | undefined): boolean {
  return status === "phasedOut"
}

// The single-variant forms, for the grids that decide per row. `settings.lifecycleStatus` is already the
// EFFECTIVE status (sku.ts resolves it through `effectiveLifecycleExpr`), so it votes over the active skus
// and has already applied the `phasingOutFrom` / `activatedUntil` fall-through. No rollup needed here.
export function isPhasedOutVariant(variant: Pick<IVariantSummary, "settings">): boolean {
  return isPhasedOutStatus(variant.settings?.lifecycleStatus)
}

export function isPhasingOutVariant(variant: Pick<IVariantSummary, "settings">): boolean {
  return variant.settings?.lifecycleStatus === "phasingOut"
}

/**
 * A variant's demand as the plan counts it. The engine still composes a number for an already
 * phased-out variant off the trailing year, but nobody plans a dead product, so it is kept out of every
 * total, the Baseline column, and what the publish writes (see `buildPublishSnapshot`).
 *
 * Every surface that SUMS demand goes through this. Applied piecemeal it let the Settings chain, the
 * Budget denominators and the Overview KPI each exceed the Total grid's own footer.
 */
export function plannedDemand(
  variant: Pick<IVariantSummary, "settings">,
  totalDemand: number,
): number {
  return isPhasedOutVariant(variant) ? 0 : totalDemand
}

/**
 * The activation date behind a phase-out, when it is a date that has actually closed.
 *
 * Because `lifecycleStatus` arrives already resolved, it cannot say whether an explicit status or the
 * `activatedUntil` fall-through produced it. Reporting the date regardless would put a future
 * deactivation on a variant a planner phased out early, which reads as a deactivation that has not
 * happened. Only a passed one is a fact about why nothing is planned.
 *
 * `asOf` is the current week start, matching the week granularity `effectiveLifecycleExpr` compares at.
 */
export function deactivatedAt(
  variant: Pick<IVariantSummary, "settings">,
  asOf: Date,
): Date | undefined {
  const until = variant.settings?.activatedUntil
  return until && until < asOf ? until : undefined
}

// Use for order/suggestion columns, not forecast columns — phasingOut still has a forecast.
export function isLifecycleInactive(entries: Pick<IVariantSummary, "settings">[]): boolean {
  return everyEntry(entries, isInactiveStatus)
}

// Mirrors the phasedOut > phasingOut > other ranking used server-side in sku.ts's grouped
// lifecycleStatus CASE — a plain frequency-based mode would pick whichever status is most
// common among a group's SKUs rather than the worst one.
export function worstLifecycleStatus(statuses: (string | null | undefined)[]): string | null {
  if (statuses.includes("phasedOut")) return "phasedOut"
  if (statuses.includes("phasingOut")) return "phasingOut"
  return null
}

export function deriveLifecycleStatus(input: {
  lifecycleStatus?: string | null
  activatedUntil?: Date | string | null
  phasingOutFrom?: Date | string | null
}): string | null {
  if (input.lifecycleStatus) return input.lifecycleStatus

  const weekStart = getStartOfCurrentWeekUTC()
  if (input.activatedUntil != null && new Date(input.activatedUntil) < weekStart) {
    return "phasedOut"
  }
  if (input.phasingOutFrom != null && new Date(input.phasingOutFrom) < addDays(weekStart, 7)) {
    return "phasingOut"
  }
  return null
}

/** The week {@link deriveLifecycleStatus} first reads `activatedUntil` as phased out. */
export function phasedOutFrom(activatedUntil: Date | string | null | undefined): Date | null {
  if (activatedUntil == null) return null
  const until = new Date(activatedUntil)
  if (Number.isNaN(until.getTime())) return null
  return addDays(convertToStartOfWeekUTC(until), 7)
}

export type LifecycleStage = "upcoming" | "phasingOut" | "phasedOut" | null

export function deriveLifecycleStage(
  input: {
    lifecycleStatus?: string | null
    activatedFrom?: Date | string | null
    activatedUntil?: Date | string | null
    phasingOutFrom?: Date | string | null
  },
  now: Date = new Date(),
): LifecycleStage {
  const status = deriveLifecycleStatus(input)
  if (status === "phasedOut") return "phasedOut"
  if (status === "phasingOut") return "phasingOut"
  if (input.activatedFrom != null && now < new Date(input.activatedFrom)) return "upcoming"
  return null
}

export function phasingOutNeverApplies(input: {
  activatedUntil?: Date | string | null
  phasingOutFrom?: Date | string | null
}): boolean {
  if (input.activatedUntil == null || input.phasingOutFrom == null) return false

  return (
    convertToStartOfWeekUTC(new Date(input.phasingOutFrom)) >
    convertToStartOfWeekUTC(new Date(input.activatedUntil))
  )
}

export type LifecycleDateField = "activatedFrom" | "phasingOutFrom" | "activatedUntil"

export function lifecycleDateIssues(input: {
  activatedFrom?: Date | string | null
  phasingOutFrom?: Date | string | null
  activatedUntil?: Date | string | null
}): Partial<Record<LifecycleDateField, string>> {
  if (input.activatedFrom == null) return {}

  const start = convertToStartOfWeekUTC(new Date(input.activatedFrom))
  const issues: Partial<Record<LifecycleDateField, string>> = {}

  if (
    input.phasingOutFrom != null &&
    convertToStartOfWeekUTC(new Date(input.phasingOutFrom)) < start
  ) {
    issues.phasingOutFrom =
      "Earlier than Activated from, so the variant starts out Phasing out and never becomes Active."
  }

  if (
    input.activatedUntil != null &&
    convertToStartOfWeekUTC(new Date(input.activatedUntil)) < start
  ) {
    issues.activatedUntil =
      "Earlier than Activated from, so the variant is Phased out before it starts."
  }

  return issues
}

export function noLifecycleDatesSet(input: {
  lifecycleStatus?: string | null
  activatedFrom?: Date | string | null
  activatedUntil?: Date | string | null
  phasingOutFrom?: Date | string | null
}): boolean {
  return (
    input.lifecycleStatus == null &&
    input.activatedFrom == null &&
    input.activatedUntil == null &&
    input.phasingOutFrom == null
  )
}

export const lifecycleDateFieldCopy = {
  phasingOutFrom: {
    label: "Phase out from",
    description:
      "From this week the product is Phasing out: it sells down to stockout and gets no suggested orders. Until then it stays active so you can buy one last time.",
  },
  activatedUntil: {
    label: "Activated until",
    description:
      "Last week the product is active. Once that week has passed it is Phased out - no demand and no suggested orders.",
  },
} as const

export const activatedUntilForPhaseOut = (phasedOutFrom: Date) =>
  subDays(convertToStartOfWeekUTC(phasedOutFrom), 1)

export const phasedOutFromNow = () => activatedUntilForPhaseOut(getStartOfCurrentWeekUTC())
export const phasingOutFromNow = () => getStartOfCurrentWeekUTC()

export function lifecycleStageProgress(
  start: Date | string | number | null | undefined,
  end: Date | string | number | null | undefined,
  now: Date = new Date(),
): number | null {
  if (start == null || end == null) return null

  const from = new Date(start).getTime()
  const to = new Date(end).getTime()
  if (!(to > from)) return null

  return Math.min(1, Math.max(0, (now.getTime() - from) / (to - from)))
}

export function lifecycleStatusColor(status: string | null | undefined): StatusColor {
  if (status === "phasedOut") return StatusColor.Error
  if (status === "phasingOut") return StatusColor.Warning
  if (status === "upcoming") return StatusColor.Neutral
  return StatusColor.Success
}

export function formatLifecycleStatus(status: string | null | undefined): string {
  switch (status) {
    case "phasingOut":
      return "Phasing out"
    case "phasedOut":
      return "Phased out"
    case "upcoming":
      return "Upcoming"
    default:
      return "Active"
  }
}

/** Null before activation or without a date. */
export function weeksSinceActivation(
  activatedFrom: Date | string | null | undefined,
): number | null {
  if (!activatedFrom) return null
  const activated = activatedFrom instanceof Date ? activatedFrom : new Date(activatedFrom)
  if (Number.isNaN(activated.getTime())) return null
  const weeks = differenceInWeeksUTC(getStartOfCurrentWeekUTC(), convertToStartOfWeekUTC(activated))
  return weeks < 0 ? null : weeks
}
